import {
  CreateMembershipTierRequest,
  MembershipBenefit,
  MembershipBenefitApi,
  MembershipBenefitVariant,
  CreateMembershipMemberRequest,
  MembershipKpiApi,
  MembershipMemberApi,
  MembershipMemberApiPaymentCycle,
  MembershipMemberApiStatus,
  MembershipViewSource,
  UpdateMembershipMemberRequest,
  MembershipPaymentCycle,
  MembershipPerk,
  MembershipStatus,
  MembershipTier,
  MembershipTierApi,
  UpdateMembershipTierRequest,
} from '@dataTypes/membership.type'
import type { MembershipKpiItem } from '@constants/Membership/membershipPage.constants'

export const inferBenefitVariant = (
  label: string,
): MembershipBenefitVariant => {
  const normalized = label.toLowerCase()
  if (normalized.includes('priority')) return 'red'
  if (normalized.includes('x-ray') || normalized.includes('panoramic')) {
    return 'green'
  }
  if (normalized.includes('%') || normalized.includes('discount')) {
    return 'indigo'
  }
  return 'blue'
}

const mapBenefitFromApi = (
  benefit: MembershipBenefitApi,
): MembershipBenefit => ({
  label: benefit.label,
  variant: benefit.variant ?? inferBenefitVariant(benefit.label),
  amount: benefit.amount ?? null,
  perkType: benefit.perk_type === 'discount' ? 'discount' : 'freebie',
  usageLimit: benefit.usage_limit ?? null,
  procedures: (benefit.procedures ?? []).map((procedure) => ({
    procedure_code: procedure.procedure_code,
    description: procedure.description ?? null,
  })),
})

export const mapMembershipTierFromApi = (
  tier: MembershipTierApi,
): MembershipTier => ({
  id: tier.membership_tier_id,
  clinicId: tier.clinic_id,
  name: tier.name,
  shieldVariant: tier.shield_variant,
  monthlyPrice: tier.monthly_price,
  annualPrice: tier.annual_price,
  enabled: tier.enabled,
  maxMembers: tier.max_members ?? null,
  description: tier.description ?? undefined,
  ...(tier.email_onboarding_message
    ? { emailOnboardingMessage: tier.email_onboarding_message }
    : {}),
  benefits: tier.benefits.map(mapBenefitFromApi),
  extraBenefitsCount: tier.extra_benefits_count ?? undefined,
})

export const mapMembershipTierListFromApi = (
  tiers: MembershipTierApi[],
): MembershipTier[] => tiers.map(mapMembershipTierFromApi)

const MEMBER_STATUS_LABELS: Record<
  MembershipMemberApiStatus,
  MembershipStatus
> = {
  active: 'Active',
  on_hold: 'On hold',
  inactive: 'Inactive',
}

const PAYMENT_CYCLE_LABELS: Record<
  MembershipMemberApiPaymentCycle,
  MembershipPaymentCycle
> = {
  monthly: 'Monthly',
  annual: 'Annual',
}

const toMembershipDisplayDate = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return value
  return `${match[2]}/${match[3]}/${match[1]}`
}

export const mapMembershipMemberFromApi = (
  member: MembershipMemberApi,
): MembershipViewSource => ({
  id: member.membership_member_id,
  membershipTierId: member.membership_tier_id,
  name: member.name,
  ...(member.patient_id ? { patientId: member.patient_id } : {}),
  tier: member.shield_variant ?? 'basic',
  memberSince: toMembershipDisplayDate(member.member_since),
  expiryDate: toMembershipDisplayDate(member.expiry_date),
  paymentCycle: PAYMENT_CYCLE_LABELS[member.payment_cycle],
  status: MEMBER_STATUS_LABELS[member.status],
  recurringAmount: Number(member.recurring_amount) || 0,
  ...(member.tier_name ? { tierName: member.tier_name } : {}),
  ...(member.status_changed_at
    ? { statusChangedAt: member.status_changed_at.slice(0, 10) }
    : {}),
  ...(member.auto_pay_method ? { autoPayMethod: member.auto_pay_method } : {}),
  ...(member.notification_channels
    ? { notificationChannels: member.notification_channels }
    : {}),
  ...(member.benefit_usage
    ? {
        benefitUsage: member.benefit_usage,
        benefitClaims: member.benefit_claims ?? [],
        totalAvailedDiscounts: member.total_availed_discounts ?? '$0',
        totalAvailedDiscountsPercent:
          member.total_availed_discounts_percent ?? 0,
        paymentHistory: (member.payment_history ?? []).map((row) => ({
          id: row.id,
          paymentDate: row.payment_date,
          paymentMethod: row.payment_method,
          paymentType: row.payment_type,
          paymentStatus: row.payment_status,
        })),
        reactivationHistory: member.reactivation_history ?? [],
      }
    : {}),
})

export const mapMembershipMemberToCreateRequest = (input: {
  clinicId: string
  patientId: string
  membershipTierId: string
  memberSince: string
  expiryDate: string
  paymentCycle: MembershipMemberApiPaymentCycle
  recurringAmount: number
  autoPayMethod?: string
  notificationChannels?: string
}): CreateMembershipMemberRequest => ({
  clinic_id: input.clinicId,
  patient_id: input.patientId,
  membership_tier_id: input.membershipTierId,
  member_since: input.memberSince,
  expiry_date: input.expiryDate,
  payment_cycle: input.paymentCycle,
  status: 'active',
  recurring_amount: input.recurringAmount,
  ...(input.autoPayMethod ? { auto_pay_method: input.autoPayMethod } : {}),
  ...(input.notificationChannels
    ? { notification_channels: input.notificationChannels }
    : {}),
})

export const mapMembershipMemberToUpdateRequest = (input: {
  membershipMemberId: string
  membershipTierId: string
  paymentCycle: MembershipMemberApiPaymentCycle
  expiryDate: string
  recurringAmount: number
  autoPayMethod?: string
  notificationChannels?: string
}): UpdateMembershipMemberRequest => ({
  membership_member_id: input.membershipMemberId,
  membership_tier_id: input.membershipTierId,
  payment_cycle: input.paymentCycle,
  expiry_date: input.expiryDate,
  recurring_amount: input.recurringAmount,
  ...(input.autoPayMethod ? { auto_pay_method: input.autoPayMethod } : {}),
  ...(input.notificationChannels
    ? { notification_channels: input.notificationChannels }
    : {}),
})

export const mapMembershipKpiFromApi = (
  kpi: MembershipKpiApi,
): MembershipKpiItem => ({
  id: kpi.id,
  label: kpi.label,
  value: kpi.value,
  unitInline: kpi.unit_inline,
  footer: kpi.footer,
  trend: kpi.trend,
})

export const mapPerksToApiPayload = (
  perks: MembershipPerk[],
): CreateMembershipTierRequest['perks'] =>
  perks
    .filter((perk) => perk.label.trim())
    .map((perk) => ({
      label: perk.label.trim(),
      amount: perk.amount.trim() || null,
      perk_type: perk.type === 'discount' ? 'discount' : 'freebie',
      usage_limit: perk.usageLimit ?? null,
      procedures: (perk.procedureOptions ?? [])
        .filter((option) => option.code.trim())
        .map((option) => ({
          procedure_code: option.code.trim(),
          description: option.description.trim() || null,
        })),
    }))

export const mapMembershipTierToCreateRequest = (
  tier: MembershipTier,
  perks: MembershipPerk[],
  clinicId: string,
): CreateMembershipTierRequest => ({
  clinic_id: clinicId,
  name: tier.name,
  shield_variant: tier.shieldVariant,
  monthly_price: tier.monthlyPrice,
  annual_price: tier.annualPrice,
  enabled: tier.enabled,
  max_members: tier.maxMembers ?? null,
  description: tier.description ?? null,
  email_onboarding_message: tier.emailOnboardingMessage?.trim() || null,
  perks: mapPerksToApiPayload(perks),
})

export const mapMembershipTierToUpdateRequest = (
  tier: MembershipTier,
  perks: MembershipPerk[],
): UpdateMembershipTierRequest => ({
  membership_tier_id: tier.id,
  ...(tier.clinicId ? { clinic_id: tier.clinicId } : {}),
  name: tier.name,
  shield_variant: tier.shieldVariant,
  monthly_price: tier.monthlyPrice,
  annual_price: tier.annualPrice,
  enabled: tier.enabled,
  max_members: tier.maxMembers ?? null,
  description: tier.description ?? null,
  email_onboarding_message: tier.emailOnboardingMessage?.trim() || null,
  perks: mapPerksToApiPayload(perks),
})
